/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, extendObservable, observable } from 'mobx';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A regex entity store factory function.
 *
 * @param id The id of the regex entity to create.
 * @param entity The regex entity data.
 * @returns A new instance of a regex entity store.
 */
export const createRegexEntityStore = (id: LuisId, entity: Partial<RegexEntityStore>): RegexEntityStore => {
    const entityStore = createEntityStore(id, { ...entity, typeId: ModelType.Regex });

    const regexStore: SubtractStore<RegexEntityStore, EntityStore> = {
        pattern: entity.pattern,

        /**
         * Updates the entity's pattern with the new
         * given pattern.
         *
         * @param newPattern The pattern to update.
         */
        updatePattern(newPattern) {
            this.pattern = newPattern;
        }
    };

    return extendObservable(entityStore, regexStore, { pattern: observable, updatePattern: action }, { deep: false });
};
